import { Link } from 'react-router'
import {
  Card,
  Stack,
  Text,
  Group,
  Box,
  Anchor,
  Loader,
  Checkbox,
  Button,
  Center,
} from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { z } from 'zod'
import { useActiveCompetitions, Competition } from './navigation/api'
import { useAllTeams } from './teams/api'
import { MatchSchema } from './matches/api'
import { useAllPlayers } from './players/api'
import { useNews } from './news/api'
import { useMemo, useState, useEffect } from 'react'
import { TeamLogoSchema } from './assets/api'
import Header from './common/components/header/Header'
import Meta from './common/Meta'
import classes from './sitemap.module.css'

// Race schema for fetching races
const RaceResultSchema = z.object({
  team_id: z.number().int().positive(),
  team_name: z.string(),
  team_logo: TeamLogoSchema,
  race_result_id: z.number().int().positive().optional(),
  score: z.number().nonnegative().optional(),
})

const RaceSchema = z.object({
  race_id: z.number().int().positive(),
  round_number: z.number().int().positive(),
  round_start: z.string().date(),
  round_status: z.enum(['ended', 'live', 'upcoming']),
  division_id: z.number().int().positive().nullable(),
  video: z
    .object({
      main: z.string().url().optional(),
    })
    .passthrough()
    .catch({ main: undefined }),
  results: z.array(RaceResultSchema).optional(),
})

interface PageNode {
  title: string
  path: string
  children?: PageNode[]
}

const LOCALSTORAGE_KEY = 'sitemap-checked-paths'

export default function Sitemap() {
  // Load checked paths from localStorage
  const [checkedPaths, setCheckedPaths] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(LOCALSTORAGE_KEY)
      return stored ? new Set(JSON.parse(stored)) : new Set()
    } catch {
      return new Set()
    }
  })

  // Save to localStorage whenever checkedPaths changes
  useEffect(() => {
    localStorage.setItem(
      LOCALSTORAGE_KEY,
      JSON.stringify(Array.from(checkedPaths))
    )
  }, [checkedPaths])

  // Fetch example data
  const { data, isLoading: loadingCompetitions } = useActiveCompetitions()
  const competitions = data?.competitions || []

  // Find competitions by type
  const matchCompetition = competitions.find(
    (c: Competition) => c.kind === 'match'
  )
  const raceCompetition = competitions.find(
    (c: Competition) => c.kind === 'race'
  )

  // Fetch teams for match competition
  const { data: matchTeams, isLoading: loadingMatchTeams } = useAllTeams({
    competition_id: matchCompetition?.competition_id ?? 0,
  })

  // Fetch teams for race competition
  const { data: raceTeams, isLoading: loadingRaceTeams } = useAllTeams({
    competition_id: raceCompetition?.competition_id ?? 0,
  })

  // Fetch matches (only if match competition exists)
  const { data: matches, isLoading: loadingMatches } = useQuery({
    queryKey: [
      'sitemapMatches',
      { competition_id: matchCompetition?.competition_id },
    ],
    queryFn: async () => {
      const response = await axios.get(
        `/api/competitions/${matchCompetition!.competition_id}/matches`
      )
      return z.array(MatchSchema).parse(response.data)
    },
    enabled: !!matchCompetition,
  })

  // Fetch races (only if race competition exists)
  const { data: races, isLoading: loadingRaces } = useQuery({
    queryKey: [
      'sitemapRaces',
      { competition_id: raceCompetition?.competition_id },
    ],
    queryFn: async () => {
      const response = await axios.get(
        `/api/competitions/${raceCompetition!.competition_id}/races`
      )
      return z.array(RaceSchema).parse(response.data)
    },
    enabled: !!raceCompetition,
  })

  // Fetch players for match competition
  const { data: matchPlayers, isLoading: loadingMatchPlayers } = useAllPlayers({
    competition_id: matchCompetition?.competition_id ?? 0,
  })

  // Fetch players for race competition
  const { data: racePlayers, isLoading: loadingRacePlayers } = useAllPlayers({
    competition_id: raceCompetition?.competition_id ?? 0,
  })

  const firstCompetitionId =
    matchCompetition?.competition_id || raceCompetition?.competition_id
  const { data: newsData, isLoading: loadingNews } = useNews({
    page: 1,
    page_size: 5,
    competition_id: firstCompetitionId || 0,
  })
  const firstNewsId = newsData?.news[0]?.news_id

  const isLoading =
    loadingCompetitions ||
    loadingMatchTeams ||
    loadingRaceTeams ||
    loadingMatches ||
    loadingRaces ||
    loadingMatchPlayers ||
    loadingRacePlayers ||
    loadingNews

  const siteMap: PageNode[] = useMemo(() => {
    const competitionPages: PageNode[] = []

    // Helper to build competition pages
    const buildCompetitionPages = (
      competition: Competition,
      teams: { team_id: number; name: string }[] | undefined,
      players: { player_id: number; name: string }[] | undefined,
      matchId: number | undefined,
      raceId: number | undefined
    ): PageNode => {
      const compId = competition.competition_id
      const isRace = competition.kind === 'race'
      const firstTeamId = teams?.[0]?.team_id
      const firstPlayerId = players?.[0]?.player_id

      const eventsSection: PageNode = isRace
        ? {
            title: 'Races',
            path: `/competitions/${compId}/races`,
            children: raceId
              ? [
                  {
                    title: `Race #${raceId}`,
                    path: `/competitions/${compId}/races/${raceId}`,
                    children: [
                      {
                        title: 'Summary',
                        path: `/competitions/${compId}/races/${raceId}/summary`,
                      },
                      {
                        title: 'Highlights',
                        path: `/competitions/${compId}/races/${raceId}/highlights`,
                      },
                    ],
                  },
                ]
              : [],
          }
        : {
            title: 'Matches',
            path: `/competitions/${compId}/matches`,
            children: matchId
              ? [
                  {
                    title: `Match #${matchId}`,
                    path: `/competitions/${compId}/matches/${matchId}`,
                    children: [
                      {
                        title: 'Summary',
                        path: `/competitions/${compId}/matches/${matchId}/summary`,
                      },
                      {
                        title: 'Watch',
                        path: `/competitions/${compId}/matches/${matchId}/watch`,
                      },
                      {
                        title: 'History',
                        path: `/competitions/${compId}/matches/${matchId}/${teams?.[0]?.team_id || 1}/history/${teams?.[1]?.team_id || 2}`,
                      },
                    ],
                  },
                ]
              : [],
          }

      return {
        title: `Competition: ${competition.name} (${isRace ? 'Race' : 'Match'})`,
        path: `/competitions/${compId}`,
        children: [
          {
            title: 'Home',
            path: `/competitions/${compId}/home`,
          },
          {
            title: 'Arena',
            path: `/competitions/${compId}/arena`,
          },
          eventsSection,
          {
            title: 'Teams',
            path: `/competitions/${compId}/teams`,
            children: firstTeamId
              ? [
                  {
                    title: `${teams?.[0]?.name || 'Team'}`,
                    path: `/competitions/${compId}/teams/${firstTeamId}`,
                    children: [
                      {
                        title: 'Overview',
                        path: `/competitions/${compId}/teams/${firstTeamId}/overview`,
                      },
                      {
                        title: isRace ? 'Races' : 'Matches',
                        path: `/competitions/${compId}/teams/${firstTeamId}/${isRace ? 'races' : 'matches'}`,
                      },
                      {
                        title: 'Players',
                        path: `/competitions/${compId}/teams/${firstTeamId}/players`,
                      },
                    ],
                  },
                ]
              : [],
          },
          {
            title: 'Players',
            path: `/competitions/${compId}/players`,
            children: firstPlayerId
              ? [
                  {
                    title: `${players?.[0]?.name || 'Player'}`,
                    path: `/competitions/${compId}/players/${firstPlayerId}`,
                  },
                ]
              : [],
          },
          {
            title: 'Ladder',
            path: `/competitions/${compId}/ladder`,
          },
          {
            title: 'Overall Ranks',
            path: `/competitions/${compId}/ranks`,
          },
          {
            title: 'Performance',
            path: `/competitions/${compId}/performance`,
            children: [
              {
                title: 'Analysis',
                path: `/competitions/${compId}/performance/analysis`,
              },
              {
                title: 'KPI Rank',
                path: `/competitions/${compId}/performance/kpi/teams`,
              },
            ],
          },
          {
            title: 'Compare',
            path: `/competitions/${compId}/compare`,
          },
          {
            title: 'Learning',
            path: `/competitions/${compId}/learning`,
          },
        ],
      }
    }

    // Add match competition if exists
    if (matchCompetition) {
      competitionPages.push(
        buildCompetitionPages(
          matchCompetition,
          matchTeams?.items,
          matchPlayers?.items,
          matches?.[0]?.match_id,
          undefined
        )
      )
    }

    // Add race competition if exists
    if (raceCompetition) {
      competitionPages.push(
        buildCompetitionPages(
          raceCompetition,
          raceTeams?.items,
          racePlayers?.items,
          undefined,
          races?.[0]?.race_id
        )
      )
    }

    return [
      {
        title: 'General Pages',
        path: '/',
        children: [
          {
            title: 'News',
            path: '/news',
            children: firstNewsId
              ? [
                  {
                    title: `${newsData?.news[0]?.title || 'Article'}`,
                    path: `/news/${firstNewsId}`,
                  },
                ]
              : [],
          },
          {
            title: 'Settings',
            path: '/settings',
          },
          {
            title: 'Clair AI Assistant',
            path: '/clair',
          },
          {
            title: 'Sitemap',
            path: '/sitemap',
          },
        ],
      },
      ...competitionPages,
      {
        title: 'Authentication',
        path: '/login',
        children: [
          {
            title: 'Login',
            path: '/login',
          },
          {
            title: 'Forgot Password',
            path: '/forgot-password',
          },
          {
            title: 'Password Reset',
            path: '/password-reset',
          },
        ],
      },
    ]
  }, [
    matchCompetition,
    raceCompetition,
    matchTeams,
    raceTeams,
    matchPlayers,
    racePlayers,
    matches,
    races,
    firstNewsId,
    newsData,
  ])

  const handleToggle = (path: string) => {
    setCheckedPaths((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(path)) {
        newSet.delete(path)
      } else {
        newSet.add(path)
      }
      return newSet
    })
  }

  const handleClearAll = () => {
    setCheckedPaths(new Set())
  }

  const renderTree = (nodes: PageNode[], level = 0) => {
    return nodes.map((node, index) => (
      <Box key={`${node.path}-${index}`} ml={level * 20}>
        <Group gap="xs" mb="xs">
          {node.path && node.path !== '/' && (
            <Checkbox
              size="xs"
              checked={checkedPaths.has(node.path)}
              onChange={() => handleToggle(node.path)}
            />
          )}
          <Text fw={level === 0 ? 700 : 500} size={level === 0 ? 'lg' : 'md'}>
            {node.title}
          </Text>
          {node.path && node.path !== '/' && (
            <Anchor component={Link} to={node.path} size="sm" c="blue">
              {node.path}
            </Anchor>
          )}
        </Group>
        {node.children && renderTree(node.children, level + 1)}
      </Box>
    ))
  }

  if (isLoading) {
    return (
      <div className={classes.container}>
        <Header title="Sitemap" back="/news" />
        <Meta title="Sitemap" />
        <Center w="100%" h="100%">
          <Loader color="#0B6B7B" />
        </Center>
      </div>
    )
  }

  return (
    <div className={classes.container}>
      <Header title="Sitemap" back="/news" />
      <Meta title="Sitemap" />
      <div className={classes.content}>
        <Stack>
          <Group justify="space-between" align="center">
            <div>
              <Text c="dimmed" size="sm">
                Complete navigation tree of all available pages. Click on any
                path to navigate to that page.
              </Text>
            </div>
            <Button
              variant="light"
              onClick={handleClearAll}
              disabled={checkedPaths.size === 0}
            >
              Clear All ({checkedPaths.size})
            </Button>
          </Group>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack>{renderTree(siteMap)}</Stack>
          </Card>

          <Text size="sm" c="dimmed" ta="center">
            Note: Example IDs are fetched from real data. Some pages may require
            authentication to access.
          </Text>
        </Stack>
      </div>
    </div>
  )
}
